import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_API_URL } from "../../consts";
import type {
  SupplierCustomValue,
  GetSupplierCustomValuesParams,
  DailyRevenueSupplierExpense,
  GetDailyRevenueSupplierExpensesParams,
  SupplierDefaultValue,
  GetSupplierDefaultValuesParams,
  MonthlyGenerationStatusMap,
} from "../../api/supplierExpensesAPI";
import type { GoodsInventoryEntry, GetGoodsInventoryParams } from "../../api/goodsInventoryAPI";

export interface SalarySummaryBulkParams {
  clientId: string;
  startDate: string;
  endDate: string;
}

export interface SalarySummaryBulkResponse {
  summaries: any[];
}

export interface GetUserPaymentsParams {
  clientId: string;
  userId: string;
  startDate: string;
  endDate: string;
  locationId?: string;
}

export interface GetSalaryHistoryParams {
  clientId: string;
  userId: string;
  months: number;
  locationId?: string;
}

export interface GetUserDetailsPaymentsParams {
  userId: string;
  startDateStr: string;
  endDateStr: string;
  locationId?: string;
}

export interface GetFinanceOverviewParams {
  clientId: string;
  months: string;
  locationId?: string;
}

export interface FinanceOverviewResponse {
  defaultValues: SupplierDefaultValue[];
  customValues: SupplierCustomValue[];
  dailyRevenueExpenses: DailyRevenueSupplierExpense[];
  generationStatus: MonthlyGenerationStatusMap;
}

// Finance/dashboard read endpoints that were each refetching on every page
// mount (navigating Finance <-> Dashboard repeatedly caused 400+ redundant
// requests in a single session and crashed the backend). RTK Query caches
// identical in-flight/recent requests across components and remounts, so
// the same data isn't re-fetched every time one of these widgets remounts.
export const financeApi = createApi({
  reducerPath: "financeApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_API_URL }),
  // If a query was fetched less than 30s ago, serve the cached result
  // instead of firing a new request on mount — this is what stops the
  // remount storm while still keeping data reasonably fresh.
  refetchOnMountOrArgChange: 30,
  endpoints: (builder) => ({
    getSupplierDefaultValues: builder.query<
      { data: SupplierDefaultValue[] },
      GetSupplierDefaultValuesParams
    >({
      query: (params) => ({ url: "/v2/supplier-default-values", params }),
    }),

    getSupplierCustomValues: builder.query<
      { data: SupplierCustomValue[] },
      GetSupplierCustomValuesParams
    >({
      query: (params) => ({ url: "/v2/supplier-custom-values", params }),
    }),

    getDailyRevenueSupplierExpenses: builder.query<
      { data: DailyRevenueSupplierExpense[] },
      GetDailyRevenueSupplierExpensesParams
    >({
      query: (params) => ({ url: "/v2/daily-revenue-supplier-expenses", params }),
    }),

    getMonthlyExpenseGenerationStatus: builder.query<
      { data: MonthlyGenerationStatusMap },
      { clientId: string; months: string }
    >({
      query: (params) => ({ url: "/v2/monthly-fixed-expenses/status", params }),
    }),

    getGoodsInventory: builder.query<{ data: GoodsInventoryEntry[] }, GetGoodsInventoryParams>({
      query: (params) => ({ url: "/v2/goods-inventory", params }),
    }),

    getUserPayments: builder.query<any, GetUserPaymentsParams>({
      query: ({ clientId, userId, startDate, endDate, locationId }) => ({
        url: `/user/${clientId}/getUserPayments/${userId}`,
        params: { startDate, endDate, locationId },
      }),
    }),

    getSalaryHistory: builder.query<{ history: any[] }, GetSalaryHistoryParams>({
      query: ({ clientId, userId, months, locationId }) => ({
        url: `/user/${clientId}/getSalaryHistory/${userId}`,
        params: { months, locationId },
      }),
    }),

    // startDateStr/endDateStr are pre-formatted (same startOfDay/endOfDay
    // Date-to-string conversion the old code used) since the backend route
    // takes them as literal path segments, not query params.
    getUserDetailsPayments: builder.query<any, GetUserDetailsPaymentsParams>({
      query: ({ userId, startDateStr, endDateStr, locationId }) => ({
        url: `/${userId}/user_details_payments/${startDateStr}/${endDateStr}`,
        params: locationId ? { locationId } : undefined,
      }),
    }),

    getSalarySummaryBulk: builder.query<SalarySummaryBulkResponse, SalarySummaryBulkParams>({
      query: (body) => ({ url: "/salary-summary/bulk", method: "POST", body }),
    }),

    // Combines default/custom/daily-revenue-expenses/generation-status —
    // the 4 calls the Finance page always fires together — into one
    // request, cutting a 4-round-trip page mount down to 1.
    getFinanceOverview: builder.query<FinanceOverviewResponse, GetFinanceOverviewParams>({
      query: (params) => ({ url: "/v2/finance-overview", params }),
    }),
  }),
});

export const {
  useGetSupplierDefaultValuesQuery,
  useGetSupplierCustomValuesQuery,
  useGetDailyRevenueSupplierExpensesQuery,
  useGetMonthlyExpenseGenerationStatusQuery,
  useGetGoodsInventoryQuery,
  useGetSalarySummaryBulkQuery,
  useGetFinanceOverviewQuery,
  useGetUserPaymentsQuery,
  useGetSalaryHistoryQuery,
  useGetUserDetailsPaymentsQuery,
} = financeApi;
